import { useState, useEffect } from "react";
import { useHabits } from "@/context/SupabaseHabitContext";
import { format, eachDayOfInterval, addMonths, getDay, isFirstDayOfMonth, startOfMonth, subMonths, addWeeks, isToday } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import HabitInfoPopup from "./HabitInfoPopup";
import { getLocalDateString } from "@/utils/dateUtils";

interface CombinedHabitGridProps {
  initialMonths?: number;
}

export default function CombinedHabitGrid({ initialMonths = 9 }: CombinedHabitGridProps) {
  const { habits } = useHabits();
  const [gridData, setGridData] = useState<(Date | null)[][]>([]);
  const [months, setMonths] = useState<{name: string, index: number}[]>([]);
  const [startDate, setStartDate] = useState(() => {
    // Start from today and go backwards (like GitHub contributions)
    const today = new Date();
    return startOfMonth(subMonths(today, initialMonths - 1));
  });
  
  // Update startDate when initialMonths changes (e.g., mobile/desktop switch)
  useEffect(() => {
    const today = new Date();
    setStartDate(startOfMonth(subMonths(today, initialMonths - 1)));
  }, [initialMonths]);
  
  // State for popup
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  
  const isMobile = useIsMobile();
  
  // Navigate through time periods
  const navigateMonths = (direction: 'forward' | 'backward') => {
    setStartDate(prevDate => {
      // Use the same amount as the grid shows
      const navigationAmount = initialMonths;
      return direction === 'forward' 
        ? addMonths(prevDate, navigationAmount)
        : addMonths(prevDate, -navigationAmount);
    });
  };
  
  // Generate the dates for the grid
  useEffect(() => {
    // Calculate end date - show up to today only
    const effectiveMonths = initialMonths;
    const today = new Date();
    // End at today, no future dates
    const endDate = today;
    
    // Generate all days in the interval
    const allDates = eachDayOfInterval({
      start: startDate,
      end: endDate,
    });
    
    // Create data structure for grid
    const monthsData: {name: string, index: number}[] = [];
    const grid: (Date | null)[][] = Array(7).fill(null).map(() => []);
    
    // Process each date and organize by day of week
    let currentMonth = "";
    let weekIndex = 0;
    
    // Fill the grid with weeks, where each column is a week
    allDates.forEach((date) => {
      const dayOfWeek = getDay(date); // 0 = Sunday, 1 = Monday, etc.
      
      // Check for start of new week
      if (dayOfWeek === 0 && grid[0].length > 0) {
        weekIndex++;
      }
      
      // Check for first day of month to add month label
      if (isFirstDayOfMonth(date)) {
        const monthName = format(date, 'MMM');
        if (monthName !== currentMonth) {
          currentMonth = monthName;
          monthsData.push({
            name: monthName,
            index: weekIndex
          });
        }
      }
      
      // Ensure all rows have this week column
      for (let i = 0; i < 7; i++) {
        // Extend the array if needed
        while (grid[i].length <= weekIndex) {
          grid[i].push(null);
        }
      }
      
      // Place the date in the grid
      grid[dayOfWeek][weekIndex] = date;
    });
    
    setGridData(grid);
    setMonths(monthsData);
  }, [startDate, initialMonths, isMobile]);

  // Get count of habits completed on a specific date
  const getHabitsCompletedCount = (date: Date | null): number => {
    if (!date) return 0;
    
    const dateString = getLocalDateString(date);
    
    return habits.reduce((count, habit) => {
      if (habit.completedDates.includes(dateString)) {
        return count + 1;
      }
      return count;
    }, 0);
  };
  
  // Get completed habits for a specific date
  const getCompletedHabits = (date: Date | null) => {
    if (!date) return [];
    
    const dateString = getLocalDateString(date);
    
    return habits.filter(habit => 
      habit.completedDates.includes(dateString)
    );
  };
  
  // Get color for a cell based on habits completed with intensity
  const getCellColor = (date: Date | null): string => {
    if (!date) return 'transparent';
    
    const dateString = getLocalDateString(date);
    const completedHabits = habits.filter(habit => 
      habit.completedDates.includes(dateString)
    );
    
    const completedCount = completedHabits.length;
    
    // No habits completed - use empty color
    if (completedCount === 0) return 'hsl(var(--habit-empty))';
    
    // Use the app's primary purple color as the base for intensity
    const baseColor = 'hsl(266, 70%, 65%)'; // Primary purple from CSS variables
    
    // Calculate intensity based on habit count
    // More habits = darker color (lower lightness)
    const maxHabits = Math.max(habits.length, 5); // Use at least 5 as max for better scaling
    const intensity = Math.min(completedCount / maxHabits, 1); // Cap at 1
    
    // Create intensity levels with better contrast
    // Grading: 1 habit, 2 habits, 3+ (3–4), 5+ (5–6), 7+
    let lightness: number;
    if (completedCount === 1) {
      lightness = 75; // Light for 1 habit
    } else if (completedCount === 2) {
      lightness = 65; // Medium-light for 2 habits
    } else if (completedCount === 3 || completedCount === 4) {
      lightness = 55; // Medium for 3+ habits (3–4 completed)
    } else if (completedCount === 5 || completedCount === 6) {
      lightness = 45; // Medium-dark for 5+ habits (5–6 completed)
    } else {
      lightness = 35; // Dark for 7+ habits
    }
    
    return `hsl(266, 70%, ${lightness}%)`;
  };

  // Get glow effect for cells
  const getCellGlow = (date: Date | null): string => {
    if (!date) return '';
    const count = getHabitsCompletedCount(date);
    return count > 0 
      ? "shadow-[0_0_5px_0px_rgba(255,255,255,0.3)] dark:shadow-[0_0_8px_0px_rgba(255,255,255,0.15)]" 
      : "";
  };
  
  // Handle cell click to show popup
  const handleCellClick = (date: Date, event: React.MouseEvent) => {
    event.stopPropagation();
    
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    setPopupPosition({ 
      x: Math.min(rect.right + 10, window.innerWidth - 330), // Prevent popup from going off-screen
      y: Math.min(rect.top, window.innerHeight - 250)
    });
    
    // If clicking the same date, close the popup
    if (selectedDate && date.getTime() === selectedDate.getTime()) {
      setSelectedDate(null);
    } else {
      setSelectedDate(date);
    }
  };

  // Weekday labels
  const weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  
  // Calculate total contributions across all habits
  const getTotalContributions = () => {
    const allCompletedDates = new Set();
    habits.forEach(habit => {
      habit.completedDates.forEach(date => {
        allCompletedDates.add(date);
      });
    });
    return allCompletedDates.size;
  };
  
  // Set cell size based on mobile or desktop to match single habit grids
  const cellSize = isMobile ? "w-3 h-3" : "w-4 h-4";
  const cellGap = isMobile ? "gap-[2px]" : "gap-1";
  
  return (
    <div className="space-y-8">
    <div className="combined-habit-grid overflow-auto border rounded-md p-4 bg-card">
      <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
        <h3 className="text-lg font-medium">Combined Progress</h3>
            <span className="text-sm text-muted-foreground">
              {getTotalContributions()} Days Contributed
            </span>
          </div>
        <div className="flex space-x-2">
          <button 
            onClick={() => navigateMonths('backward')}
            className="p-1 rounded hover:bg-accent"
            aria-label="Previous months"
          >
            &larr;
          </button>
          <button 
            onClick={() => navigateMonths('forward')}
            className="p-1 rounded hover:bg-accent"
            aria-label="Next months"
          >
            &rarr;
          </button>
        </div>
      </div>
      
      <div className="overflow-x-auto w-full">
        <div className="min-w-max">
          {/* Month labels */}
          <div className="flex h-6 relative mb-1">
            <div className={`${isMobile ? 'w-4' : 'w-6'} mr-2`}></div> {/* Spacer for alignment with weekday column */}
            <div className="flex-1 relative">
              {months.map((month, i) => (
                <div 
                  key={i} 
                  className="absolute text-xs text-muted-foreground"
                  style={{ 
                    left: `${month.index * (isMobile ? 15 : 20)}px`,
                  }}
                >
                  {month.name}
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex">
            {/* Weekday labels */}
            <div className={`flex flex-col ${isMobile ? 'w-4' : 'w-6'} mr-2`}>
              {weekdays.map((day, index) => (
                <div
                  key={index}
                  className="flex items-center justify-center text-xs text-muted-foreground leading-none mb-1"
                  style={{ height: isMobile ? "15px" : "20px" }}
                >
                  {day}
                </div>
              ))}
            </div>
            
            {/* Grid cells */}
            <div className="flex flex-col">
              {gridData.map((row, rowIndex) => (
                <div 
                  key={`row-${rowIndex}`} 
                  className={`flex items-center ${cellGap} mb-1`} 
                  style={{ height: isMobile ? "15px" : "20px" }}
                >
                  {row.map((date, colIndex) => {
                    const habitsCount = getHabitsCompletedCount(date);
                    const dateStr = date ? format(date, 'MMMM d, yyyy') : '';
                    const completionStatus = habitsCount > 0 
                      ? `${habitsCount} habit${habitsCount > 1 ? 's' : ''} completed` 
                      : 'No habits completed';
                    const glowEffect = getCellGlow(date);
                    const hoverEffect = "hover:scale-125 hover:z-10 hover:shadow-md";
                    const isTodayDate = date ? isToday(date) : false;
                    const todayBorder = isTodayDate ? "ring-2 ring-primary ring-offset-1 ring-offset-background" : "";
                    
                    return (
                      <div
                        key={`cell-${rowIndex}-${colIndex}`}
                        className={`${cellSize} rounded-sm transition-all ${glowEffect} ${hoverEffect} ${todayBorder} cursor-pointer grid-cell`}
                        style={{ 
                          backgroundColor: getCellColor(date),
                          visibility: date ? 'visible' : 'hidden'
                        }}
                        title={date ? `${dateStr} - ${completionStatus}${isTodayDate ? ' (Today)' : ''}` : ''}
                        data-date={dateStr}
                        onClick={date ? (e) => handleCellClick(date, e) : undefined}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Legend */}
      <div className="mt-6 flex flex-wrap gap-4 text-sm">
        <span className="text-muted-foreground mr-2">Intensity:</span>
        <div className="flex items-center gap-2">
          <div 
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: 'hsl(266, 70%, 75%)' }}
          />
          <span>1 habit</span>
        </div>
        <div className="flex items-center gap-2">
          <div 
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: 'hsl(266, 70%, 65%)' }}
          />
          <span>2 habits</span>
        </div>
        <div className="flex items-center gap-2">
          <div 
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: 'hsl(266, 70%, 55%)' }}
          />
          <span>3+ habits</span>
        </div>
        <div className="flex items-center gap-2">
          <div 
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: 'hsl(266, 70%, 45%)' }}
          />
          <span>5+ habits</span>
        </div>
        <div className="flex items-center gap-2">
          <div 
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: 'hsl(266, 70%, 35%)' }}
          />
          <span>7+ habits</span>
        </div>
      </div>
      
      {/* Popup for habit details */}
      {selectedDate && (
        <>
          <HabitInfoPopup
            date={selectedDate}
            habits={habits}
            completedHabits={getCompletedHabits(selectedDate)}
            position={popupPosition}
            onClose={() => setSelectedDate(null)}
          />
          {/* Backdrop for closing popup when clicking outside */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setSelectedDate(null)}
          />
        </>
      )}
      </div>
    </div>
  );
}
